/**
 * UTM Parameter Capture
 * Extracts and persists UTM parameters for marketing attribution
 */

import { ANALYTICS_STORAGE_KEYS } from "../../constants/analytics";
import {
  isExternalReferrer,
  sanitizeReferrer,
  sanitizeUtmField,
  sanitizeUtmParameters,
  UTM_PARAM_MAX_LENGTH,
} from "./utmSanitize";

export interface UTMParameters {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  landing_page?: string;
}

/**
 * Capture UTM parameters from URL and persist for session attribution
 * @returns UTM parameters object with all captured values
 */
export function captureUTMParameters(): UTMParameters {
  const params = new URLSearchParams(window.location.search);

  // Start with previously stored parameters to avoid overwriting
  const stored = getStoredUTMParameters();

  const utm: UTMParameters = {
    ...stored,
    landing_page: window.location.pathname,
  };

  // Capture UTM parameters from URL query string
  const utmKeys = ["source", "medium", "campaign", "term", "content"];
  let hasNewUtms = false;

  utmKeys.forEach((key) => {
    const value = params.get(`utm_${key}`);
    if (value) {
      const sanitized = sanitizeUtmField(value, UTM_PARAM_MAX_LENGTH);
      if (sanitized) {
        utm[`utm_${key}` as keyof UTMParameters] = sanitized;
        hasNewUtms = true;
      }
    }
  });

  // Capture referrer (where the user came from)
  // Only update referrer if it's external (not our own site) to avoid overwriting original referrer
  if (
    document.referrer &&
    isExternalReferrer(document.referrer, window.location.hostname)
  ) {
    const referrer = sanitizeReferrer(document.referrer);
    if (referrer) {
      utm.referrer = referrer;
      hasNewUtms = true;
    }
  }

  // Persist UTM params to localStorage for session attribution
  // Only persist if we found new UTMs/referrer OR if we didn't have stored ones yet
  if (hasNewUtms || Object.keys(stored).length === 0) {
    try {
      localStorage.setItem(
        ANALYTICS_STORAGE_KEYS.UTM_PARAMS,
        JSON.stringify(sanitizeUtmParameters(utm)),
      );
    } catch (error) {
      // Silent fail - localStorage might not be available
      console.warn("[UTM] Failed to persist UTM parameters:", error);
    }
  }

  return sanitizeUtmParameters(utm);
}

/**
 * Retrieve previously captured UTM parameters from localStorage
 * Useful for attributing conversions to original marketing source
 * @returns Previously captured UTM parameters or empty object
 */
export function getStoredUTMParameters(): UTMParameters {
  try {
    const stored = localStorage.getItem(ANALYTICS_STORAGE_KEYS.UTM_PARAMS);
    if (stored) {
      return sanitizeUtmParameters(JSON.parse(stored) as UTMParameters);
    }
  } catch (error) {
    // Silent fail - invalid JSON or localStorage not available
    console.warn("[UTM] Failed to retrieve stored UTM parameters:", error);
  }
  return {};
}

/**
 * Clear stored UTM parameters
 * Useful for testing or after conversion attribution
 */
export function clearStoredUTMParameters(): void {
  try {
    localStorage.removeItem(ANALYTICS_STORAGE_KEYS.UTM_PARAMS);
  } catch (error) {
    console.warn("[UTM] Failed to clear UTM parameters:", error);
  }
}

